import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight,
  Layers3,
  Loader2,
  Search,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import type { AdminContent } from '@/api/admin'
import { ContentLevelBadge, ContentPickerModal } from '@/components/content/ContentPickerModal'
import { getTemplates } from '@/api/configuration'
import * as configV2Api from '@/api/configurationV2'
import type { ContentPathItemDto } from '@/types/configurationV2'

function ContentPath({ path, currentContentId }: { path: ContentPathItemDto[]; currentContentId: number }) {
  const ancestors = path.filter((item) => item.contentId !== currentContentId)

  if (ancestors.length === 0) {
    return <span className="text-xs text-slate-400">상위 콘텐츠 없음</span>
  }

  return (
    <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
      {ancestors.map((item, index) => (
        <span key={item.contentId} className="inline-flex min-w-0 items-center gap-1">
          {index > 0 ? <ChevronRight className="h-3 w-3 shrink-0 text-slate-300" /> : null}
          <span className="max-w-52 truncate" title={item.contentName}>
            {item.contentName}
          </span>
        </span>
      ))}
    </div>
  )
}

export default function ContentConfigMapping() {
  const queryClient = useQueryClient()
  const [selectedContent, setSelectedContent] = useState<AdminContent | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [isPickerOpen, setIsPickerOpen] = useState(false)

  const { data: mappings = [], isLoading } = useQuery({
    queryKey: ['content-config-mappings'],
    queryFn: configV2Api.getContentMappings,
  })

  const { data: templateResponse } = useQuery({
    queryKey: ['configuration-templates'],
    queryFn: getTemplates,
  })
  const templates = templateResponse?.templates ?? []

  const saveMutation = useMutation({
    mutationFn: ({ contentId, templateId }: { contentId: number; templateId: number }) =>
      configV2Api.setContentMapping(contentId, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-config-mappings'] })
      toast.success('매핑이 저장되었습니다')
      setSelectedContent(null)
      setSelectedTemplateId('')
    },
    onError: () => toast.error('저장에 실패했습니다'),
  })

  const deleteMutation = useMutation({
    mutationFn: configV2Api.deleteContentMapping,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-config-mappings'] })
      toast.success('매핑이 삭제되었습니다')
    },
    onError: () => toast.error('삭제에 실패했습니다'),
  })

  const alreadyMapped = selectedContent
    ? mappings.some((mapping) => mapping.contentId === selectedContent.id)
    : false

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedContent || !selectedTemplateId) return
    saveMutation.mutate({
      contentId: selectedContent.id,
      templateId: Number(selectedTemplateId),
    })
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-bold text-slate-900">Content-Configuration 매핑</h3>
        <p className="mt-1 text-sm text-slate-500">콘텐츠 계층별로 기본 Learning Configuration 템플릿을 연결합니다.</p>
      </div>

      <form onSubmit={handleSubmit} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(240px,1fr)_auto] lg:items-end">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-600">콘텐츠</label>
            <button
              type="button"
              onClick={() => setIsPickerOpen(true)}
              className={`flex min-h-11 w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition ${
                selectedContent
                  ? 'border-blue-300 bg-blue-50/50 ring-2 ring-blue-100'
                  : 'border-slate-300 bg-white hover:border-blue-400 hover:bg-blue-50/30'
              }`}
            >
              {selectedContent ? (
                <>
                  <ContentLevelBadge level={selectedContent.hierarchyLevel} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-slate-800">{selectedContent.title}</span>
                    <span className="block text-xs text-slate-400">Content #{selectedContent.id}</span>
                  </span>
                  <span className="text-xs font-medium text-blue-600">변경</span>
                </>
              ) : (
                <>
                  <span className="rounded-md bg-slate-100 p-1.5 text-slate-500"><Search className="h-4 w-4" /></span>
                  <span className="text-sm font-medium text-slate-500">콘텐츠를 검색해 선택하세요</span>
                </>
              )}
            </button>
            {alreadyMapped ? (
              <p className="mt-1.5 text-xs font-medium text-amber-600">이미 매핑된 콘텐츠입니다. 저장하면 템플릿이 변경됩니다.</p>
            ) : null}
          </div>

          <div>
            <label htmlFor="content-config-template" className="mb-1.5 block text-xs font-semibold text-slate-600">템플릿</label>
            <select
              id="content-config-template"
              value={selectedTemplateId}
              onChange={(event) => setSelectedTemplateId(event.target.value)}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            >
              <option value="">템플릿 선택</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>{template.name}</option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={!selectedContent || !selectedTemplateId || saveMutation.isPending}
            className="flex h-11 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {alreadyMapped ? '매핑 변경' : '매핑 저장'}
          </button>
        </div>
      </form>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h4 className="text-sm font-bold text-slate-800">현재 매핑</h4>
            <p className="mt-0.5 text-xs text-slate-400">{mappings.length}개 콘텐츠에 템플릿이 연결되어 있습니다.</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-14 text-sm text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" /> 매핑을 불러오는 중입니다
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-slate-50/80">
                <tr className="border-b border-slate-200 text-left text-xs font-semibold text-slate-500">
                  <th className="px-5 py-3">콘텐츠</th>
                  <th className="px-5 py-3">상위 콘텐츠 경로</th>
                  <th className="px-5 py-3">적용 템플릿</th>
                  <th className="w-16 px-5 py-3"><span className="sr-only">작업</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {mappings.map((mapping) => (
                  <tr key={mapping.contentId} className="transition-colors hover:bg-slate-50/70">
                    <td className="px-5 py-4">
                      <div className="flex items-start gap-3">
                        {mapping.contentHierarchyLevel ? <ContentLevelBadge level={mapping.contentHierarchyLevel} /> : null}
                        <div className="min-w-0">
                          <p className="max-w-72 truncate font-semibold text-slate-800" title={mapping.contentName ?? undefined}>
                            {mapping.contentName ?? '삭제되었거나 찾을 수 없는 콘텐츠'}
                          </p>
                          <p className="mt-0.5 text-xs tabular-nums text-slate-400">Content #{mapping.contentId}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <ContentPath path={mapping.contentPath ?? []} currentContentId={mapping.contentId} />
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex rounded-md bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-700">
                        {mapping.templateName ?? `Template #${mapping.learningConfigurationId}`}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => deleteMutation.mutate(mapping.contentId)}
                        disabled={deleteMutation.isPending}
                        aria-label={`${mapping.contentName ?? `Content ${mapping.contentId}`} 매핑 삭제`}
                        title="매핑 삭제"
                        className="rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {mappings.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-14 text-center">
                      <Layers3 className="mx-auto h-8 w-8 text-slate-300" />
                      <p className="mt-3 text-sm font-medium text-slate-500">매핑된 콘텐츠가 없습니다</p>
                      <p className="mt-1 text-xs text-slate-400">위에서 콘텐츠와 템플릿을 선택해 첫 매핑을 추가하세요.</p>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isPickerOpen ? (
        <ContentPickerModal
          onClose={() => setIsPickerOpen(false)}
          onSelect={(content) => {
            setSelectedContent(content)
            setIsPickerOpen(false)
          }}
        />
      ) : null}
    </div>
  )
}
